import { IAppDataContext } from "../contexts/AppContext";
import { IClientDataResponse } from "../_services/interface/client.interface";
import { ICertificate } from "../_services/interface/certificate.interface";
import { certificateHasJurisdiction, Jurisdiction } from "../utils/certificateJurisdiction";
import { subpaginasDoCliente } from "../pages/TaxCurrentAccount/contaCorrenteSlugs";
import { checkCollaboratorPermission } from "../pages/Users/utils/checkPermission";
import { hasCredentialsModule } from "../utils/credentialsModule";

export type { Jurisdiction };

/**
 * Per-route access predicates. Each mirrors the exact condition the Sidebar uses
 * to SHOW the corresponding menu item, so a page is reachable by URL iff its menu
 * item would be visible. Permission (e.g. "kanban.read") is checked separately by
 * RequirePermission; these cover the PRODUCT gate — both "the company has the
 * product" and (for certificates) "the user has the product".
 */

// GEN-4021: os resources do cliente só valem depois que o getResources respondeu.
// Antes disso (ou se ele falhou) o `[]` do contexto é "não sei", não "o cliente
// não tem controle por jurisdição" — e tratá-lo como o segundo liberava as três.
// `null` = desconhecido.
export type ResourcesStatus = "loading" | "loaded" | "error";
/** Resultado do getResources e o cliente a que ele pertence. */
export interface IResourcesState {
  guid?: string;
  status: ResourcesStatus;
  resources: string[];
}

const NO_RESOURCES: string[] = [];
export const RESOURCES_UNKNOWN: IResourcesState = { status: "loading", resources: NO_RESOURCES };

// O resultado só vale para o cliente a que pertence: para qualquer outro (inclusive no render da
// troca de cliente, antes de o getResources do novo sair) é "carregando" com lista vazia.
export const resourcesViewFor = (
  state: IResourcesState,
  guid: string | undefined
): Pick<IResourcesState, "status" | "resources"> => (guid && state.guid === guid ? state : RESOURCES_UNKNOWN);

// Falha num refresh do MESMO cliente mantém os resources que já tinham chegado; sem eles, erro.
export const settleResourcesFailure = (state: IResourcesState, guid: string): IResourcesState =>
  state.guid === guid && state.status === "loaded" ? state : { guid, status: "error", resources: NO_RESOURCES };

export const loadedResources = (d: Pick<IAppDataContext, "resources" | "resourcesLoaded">): string[] | null =>
  d.resourcesLoaded ? d.resources : null;

// Mirrors the Sidebar's per-jurisdiction filtering: with the jurisdiction
// resource enabled, only the jurisdictions the collaborator can see are visible;
// without it, all three are; with the resources still unknown (`null`), none —
// fail-closed. Single source of truth for both the menu and the route/query guards.
export const getVisibleJurisdictions = (
  collaborator: IClientDataResponse | undefined,
  resources: string[] | null
): Jurisdiction[] => {
  const all: Jurisdiction[] = ["federal", "state", "municipal"];
  if (resources == null) {
    return [];
  }
  if (!resources.includes("jurisdiction_management_permission")) {
    return all;
  }
  return all.filter(
    (j) =>
      (j === "federal" && collaborator?.canSeeJurisdictionFederal) ||
      (j === "state" && collaborator?.canSeeJurisdictionState) ||
      (j === "municipal" && collaborator?.canSeeJurisdictionMunicipal)
  );
};

// Mirrors Sidebar.collaboratorHasAccessAnyJurisdiction: when the jurisdiction
// resource is enabled, the collaborator must see at least one jurisdiction.
export const hasAnyJurisdictionAccess = (
  collaborator: IClientDataResponse | undefined,
  resources: string[] | null
): boolean => getVisibleJurisdictions(collaborator, resources).length > 0;

// "ele não tiver o produto" for certificates: the collaborator must own at least
// one product (userProducts) or be admin/owner.
const hasUserProductsOrAdmin = (collaborator?: IClientDataResponse): boolean =>
  !!(
    (collaborator?.userProducts && collaborator.userProducts.length > 0) ||
    collaborator?.permissions.includes("org.admin") ||
    collaborator?.permissions.includes("org.owner")
  );

// Whether a certificate `type` (slug) would be shown in the Sidebar for this
// user: company has the product (PJ or PF), the collaborator owns the product
// (or is admin/owner), and it has at least one visible jurisdiction. Used to
// reject deep-link `?type=` values the menu would never expose.
export const isCertificateTypeVisible = (
  slug: string,
  certificates: ICertificate[],
  currentClient: IClientDataResponse | undefined,
  collaborator: IClientDataResponse | undefined,
  resources: string[] | null
): boolean => {
  const certificate = certificates.find((c) => c.slug === slug);
  if (!certificate || !currentClient || !hasUserProductsOrAdmin(collaborator)) {
    return false;
  }

  const products = (currentClient.products ?? {}) as Record<string, boolean>;
  const productsCpf = (currentClient.products_cpf ?? {}) as Record<string, boolean>;

  const companyHasProduct =
    !!products[certificate.slug as string] ||
    !!certificate.children?.some((child) => products[child.slug as string]) ||
    !!productsCpf[certificate.slug as string] ||
    !!certificate.children?.some((child) => productsCpf[child.slug as string]);

  const isAdminOrOwner =
    !!collaborator?.permissions.includes("org.admin") || !!collaborator?.permissions.includes("org.owner");
  const userHasProduct =
    (!!collaborator?.userProducts &&
      (collaborator.userProducts.includes(certificate.slug as string) ||
        !!certificate.children?.some((child) => collaborator.userProducts?.includes(child.slug as string)))) ||
    isAdminOrOwner;

  const visibleJurisdictions = getVisibleJurisdictions(collaborator, resources);
  const hasVisibleJurisdiction = visibleJurisdictions.some((j) => certificateHasJurisdiction(certificate, j));

  return companyHasProduct && userHasProduct && hasVisibleJurisdiction;
};

/**
 * GEN-4021 — um gate de rota que LÊ os resources do cliente precisa esperar o `getResources`
 * responder; os demais, não. O gate declara isso em si mesmo (`usesResources`), para a rota não
 * precisar lembrar: quem usa `hasAnyJurisdictionAccess`/`getVisibleJurisdictions` nasce marcado.
 */
export type RouteGate = ((d: IAppDataContext) => boolean) & { usesResources?: boolean };

const needsResources = (gate: (d: IAppDataContext) => boolean): RouteGate =>
  Object.assign(gate, { usesResources: true as const });

export const canAccessKanban = (d: IAppDataContext): boolean => !!d.currentClient?.products?.kanban;

export const canAccessBatchCertificates = (d: IAppDataContext): boolean =>
  !!d.currentClient?.products?.batch_certificate_on_demand;

export const canAccessCertidoes: RouteGate = needsResources(
  (d: IAppDataContext): boolean =>
    d.hasCertificate &&
    hasAnyJurisdictionAccess(d.collaborator, loadedResources(d)) &&
    hasUserProductsOrAdmin(d.collaborator)
);

export const canAccessDte: RouteGate = needsResources(
  (d: IAppDataContext): boolean =>
    !!(
      d.currentClient?.products?.dte_federal ||
      d.currentClient?.products?.dte_state ||
      d.currentClient?.products?.dte_municipal
    ) && hasAnyJurisdictionAccess(d.collaborator, loadedResources(d))
);

/**
 * GEN-3984 — `hasTaxCurrentAccountProduct`: o módulo Conta Corrente existe para quem contratou QUALQUER
 * consulta dele (Situação Fiscal `tax-status` OU CADIN Federal `cadin_federal`); cada sub-página confere
 * a sua (`subpaginasDoCliente`). Derivado no `AppShell` a partir dos produtos do cliente.
 */
export const temProdutoContaCorrente = (products: Record<string, unknown> | null | undefined): boolean =>
  subpaginasDoCliente(products).length > 0;

export const canAccessTaxCurrentAccount: RouteGate = needsResources(
  (d: IAppDataContext): boolean =>
    d.hasTaxCurrentAccountProduct &&
    hasAnyJurisdictionAccess(d.collaborator, loadedResources(d)) &&
    // ao menos uma sub-página visível (o CADIN Federal exige a jurisdição federal)
    subpaginasDoCliente(
      d.currentClient?.products as Record<string, unknown> | undefined,
      getVisibleJurisdictions(d.collaborator, loadedResources(d))
    ).length > 0
);

export const canAccessPersona = (d: IAppDataContext): boolean => d.hasCpfProduct;

export const canAccessDigitalCertificate = (d: IAppDataContext): boolean => !!d.currentClient?.useDigitalCertificate;

// GEN-4162: régua do catálogo (`modules.credentials` do /auth/validate), a mesma do menu.
export const canAccessCredentials = (d: IAppDataContext): boolean =>
  hasCredentialsModule(d.currentClient, d.hasTaxCurrentAccountProduct);

// Kanban 2.0 (rollout gradual): a flag por cliente é o product `kanban2` — decisão
// provisória (E1 do plano de rollout em aberto; espelha o `product: 'kanban2'` do
// catálogo de permissões no backend, PR #1942). Nenhum cliente tem o product hoje,
// então a rota/menu ficam invisíveis até o seed do rollout.
export const canAccessKanbanV2 = (d: IAppDataContext): boolean =>
  !!(d.currentClient?.products as Record<string, boolean> | undefined)?.kanban2;

/**
 * Automações — módulo próprio na sidebar.
 *
 * GEN-4080 r4 (liberação gradual): a Taxly libera o módulo cliente a cliente pela chave
 * `automations_released` (só a Taxly grava, pelo módulo Scripts do backend). Liberado, o próprio
 * cliente liga e desliga `automations` em Configurações de produtos. Os dois precisam estar ligados.
 *
 * ⚠️ O Kanban 2.0 deixou de abrir Automações sozinho (decisão do Thiago, 27/10/2026): o `|| kanban2`
 * de antes liberava o módulo para quem a Taxly ainda não liberou, e o backend responde 403
 * (`AUTOMATIONS_NOT_RELEASED`) nessas rotas.
 */
export const canAccessAutomacoes = (d: Pick<IAppDataContext, "currentClient">): boolean => {
  const p = d.currentClient?.products as Record<string, boolean> | undefined;
  // `=== true`, igual ao backend: valor que não seja booleano verdadeiro (string, 1) não libera.
  return p?.automations_released === true && p?.automations === true;
};

/**
 * Pode ver os ALERTAS personalizados (sino com "+" da barra, busca global, aba de Configurações ›
 * Notificações, porta dos alertas em `/automacoes`)? GEN-4080 r4: os alertas são um tipo de automação
 * e seguem a mesma liberação — sem ela, tudo some (o backend também recusa `/alert-rules*`).
 */
export const podeVerAlertas = (d: Pick<IAppDataContext, "currentClient" | "validation">): boolean => {
  const guid = d.currentClient?.guid_client;
  if (!guid || !canAccessAutomacoes(d)) return false;
  return checkCollaboratorPermission(d.validation, guid, "alerts.read");
};

/** Abas da lista de Automações (GEN-4080). Vêm do `?tipo=` da URL e descem para a lib como `tipo`. */
export type TipoDeAutomacao = "alertas" | "agendamentos" | "acoes";

const TIPOS_DE_AUTOMACAO: readonly TipoDeAutomacao[] = ["alertas", "agendamentos", "acoes"];

/** `?tipo=` da URL → tipo conhecido. Valor desconhecido vale como ausente (aba "Todas"), nunca vaza para a lib. */
export const tipoDeAutomacaoDaUrl = (valor: string | null | undefined): TipoDeAutomacao | undefined =>
  TIPOS_DE_AUTOMACAO.find((t) => t === valor);

/**
 * Quem pode abrir `/automacoes` — GEN-4080.
 *
 * Duas portas, ambas atrás da liberação do módulo (`canAccessAutomacoes`, GEN-4080 r4):
 *  - a de sempre: `automations.read` — vê a lista inteira;
 *  - a dos ALERTAS: `alerts.read` (`podeVerAlertas`). Os alertas personalizados viraram um tipo de
 *    automação e a página deles é a lista filtrada na aba Alertas. Quem só administra alertas abre a
 *    mesma página — a lib busca apenas o que a pessoa pode ler e só mostra as abas que ela lê.
 *
 * Sem a liberação nenhuma porta abre: a rota redireciona como as demais rotas sem acesso.
 */
export const podeAbrirAutomacoes = (d: IAppDataContext): boolean => {
  const guid = d.currentClient?.guid_client;
  if (!guid || !canAccessAutomacoes(d)) return false;
  if (checkCollaboratorPermission(d.validation, guid, "automations.read")) return true;
  // Qualquer aba: quem só lê alertas pode clicar em "Todas" (a lib mostra só os alertas e esconde as abas
  // que a pessoa não lê). Por isso a régua não olha a aba: barrar por ela mandaria esse clique para o Dashboard.
  return podeVerAlertas(d);
};

/**
 * Destino do item "Automações" da sidebar: a lista inteira para quem tem Automações; a aba Alertas para
 * quem só tem alertas; `null` (item escondido) para quem não tem nenhum dos dois ou sem a liberação.
 */
export const destinoDeAutomacoesNoMenu = (d: Pick<IAppDataContext, "currentClient" | "validation">): string | null => {
  const guid = d.currentClient?.guid_client;
  if (!guid || !canAccessAutomacoes(d)) return null;
  if (checkCollaboratorPermission(d.validation, guid, "automations.read")) return "/automacoes";
  if (podeVerAlertas(d)) return "/automacoes?tipo=alertas";
  return null;
};

// Taxly ISS (GEN-2972, piloto Statkraft): flag por cliente é o product `iss`
// (seed em prisma/migrations-manual/2026-07-10-iss-seed-statkraft.sql no backend).
export const canAccessIss = (d: IAppDataContext): boolean =>
  !!(d.currentClient?.products as Record<string, boolean> | undefined)?.iss;

// Base Cadastral (GEN-3162, cliente âncora Votorantim): flag por cliente é o
// product `registry` — espelha o `product: 'registry'` do catálogo de permissões
// no backend. Sem o product no cliente, rota e item de menu ficam invisíveis.
export const canAccessBaseCadastral = (d: IAppDataContext): boolean =>
  !!(d.currentClient?.products as Record<string, boolean> | undefined)?.registry;
